import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Settings, Mail, MessageSquare, Link as LinkIcon, Loader2, Save, ExternalLink, CreditCard, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut } from '../api';
import { formatApiError } from '../lib/apiError';
import { AdminSenderIdRequests } from '../components/AdminSenderIdRequests';
import { PlatformSendsPanel } from '../components/PlatformSendsPanel';

export const AdminSettingsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Platform settings state
  const [platformSettings, setPlatformSettings] = useState({
    smtp2go_api_key: '',
    smtp2go_sender_email: '',
    smtp2go_sender_name: '',
    clicksend_username: '',
    clicksend_api_key: '',
    ryftpay_mode: 'sandbox',
    ryftpay_sandbox_api_key: '',
    ryftpay_production_api_key: '',
    ryftpay_sandbox_public_key: '',
    ryftpay_production_public_key: '',
    pass_processing_fees_to_subaccount: false
  });
  const [smtp2goConfigured, setSmtp2goConfigured] = useState(false);
  const [clicksendConfigured, setClicksendConfigured] = useState(false);
  const [ryftpayConfigured, setRyftpayConfigured] = useState(false);
  const [ryftpaySandboxConfigured, setRyftpaySandboxConfigured] = useState(false);
  const [ryftpayProductionConfigured, setRyftpayProductionConfigured] = useState(false);
  const [ryftpaySandboxPublicKeyConfigured, setRyftpaySandboxPublicKeyConfigured] = useState(false);
  const [ryftpayProductionPublicKeyConfigured, setRyftpayProductionPublicKeyConfigured] = useState(false);
  
  // Affiliate settings state
  const [affiliateSettings, setAffiliateSettings] = useState({
    brevo_affiliate_link: ''
  });

  // Iter-46n: test-SMS state for the SMS tab.
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);

  // Iter-46o: test-email state for the Email tab.
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [platformRes, affiliateRes] = await Promise.all([
        apiGet('/api/admin/platform-settings').catch(() => ({})),
        apiGet('/api/admin/settings/affiliate').catch(() => ({}))
      ]);
      
      setPlatformSettings({
        smtp2go_api_key: '',
        smtp2go_sender_email: platformRes.smtp2go_sender_email || '',
        smtp2go_sender_name: platformRes.smtp2go_sender_name || 'StudioScheduler',
        clicksend_username: '',
        clicksend_api_key: '',
        clicksend_sender_id: platformRes.clicksend_sender_id || '',
        ryftpay_mode: platformRes.ryftpay_mode || 'sandbox',
        ryftpay_sandbox_api_key: '',
        ryftpay_production_api_key: '',
        ryftpay_sandbox_public_key: '',
        ryftpay_production_public_key: '',
        pass_processing_fees_to_subaccount: !!platformRes.pass_processing_fees_to_subaccount
      });
      setSmtp2goConfigured(platformRes.smtp2go_configured || false);
      setClicksendConfigured(platformRes.clicksend_configured || false);
      setRyftpayConfigured(platformRes.ryftpay_configured || false);
      setRyftpaySandboxConfigured(platformRes.ryftpay_sandbox_configured || false);
      setRyftpayProductionConfigured(platformRes.ryftpay_production_configured || false);
      setRyftpaySandboxPublicKeyConfigured(platformRes.ryftpay_sandbox_public_key_configured || false);
      setRyftpayProductionPublicKeyConfigured(platformRes.ryftpay_production_public_key_configured || false);
      
      setAffiliateSettings({
        brevo_affiliate_link: affiliateRes.brevo_affiliate_link || ''
      });
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlatformSettings = async () => {
    setSaving(true);
    try {
      await apiPut('/api/admin/platform-settings', { body: {
        smtp2go_api_key: platformSettings.smtp2go_api_key || undefined,
        smtp2go_sender_email: platformSettings.smtp2go_sender_email,
        smtp2go_sender_name: platformSettings.smtp2go_sender_name,
        clicksend_username: platformSettings.clicksend_username || undefined,
        clicksend_api_key: platformSettings.clicksend_api_key || undefined,
        clicksend_sender_id: platformSettings.clicksend_sender_id || undefined,
        ryftpay_mode: platformSettings.ryftpay_mode,
        ryftpay_sandbox_api_key: platformSettings.ryftpay_sandbox_api_key || undefined,
        ryftpay_production_api_key: platformSettings.ryftpay_production_api_key || undefined,
        ryftpay_sandbox_public_key: platformSettings.ryftpay_sandbox_public_key || undefined,
        ryftpay_production_public_key: platformSettings.ryftpay_production_public_key || undefined,
        pass_processing_fees_to_subaccount: platformSettings.pass_processing_fees_to_subaccount
      } });
      toast.success('Platform settings saved!');
      fetchSettings();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAffiliateSettings = async () => {
    setSaving(true);
    try {
      // Typed SDK — body shape is checked against AdminAffiliateSettings.
      await apiPut('/api/admin/settings/affiliate', { body: affiliateSettings });
      toast.success('Affiliate settings saved!');
    } catch (e) {
      const detail = formatApiError(e, '');
      toast.error(detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestSms = async () => {
    if (!testPhone.trim()) {
      toast.error('Enter a phone number first (E.164 format, e.g. +353871234567).');
      return;
    }
    if (!clicksendConfigured) {
      toast.error('Save your ClickSend username + API key before sending a test.');
      return;
    }
    setTestSending(true);
    const loadingId = toast.loading('Sending test SMS…');
    try {
      const data = await apiPost('/api/messaging/sms/test', {
        body: {
          to_phone: testPhone.trim(),
          content: 'StudioScheduler test — your ClickSend integration is live.',
        },
      });
      toast.dismiss(loadingId);
      toast.success(data.message || 'Test SMS sent — check your phone.');
    } catch (e) {
      toast.dismiss(loadingId);
      toast.error(formatApiError(e, 'Test SMS failed'));
    } finally {
      setTestSending(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddr.trim()) {
      toast.error('Enter an email address first.');
      return;
    }
    if (!smtp2goConfigured) {
      toast.error('Save your SMTP2Go API key + sender email before sending a test.');
      return;
    }
    setTestEmailSending(true);
    const loadingId = toast.loading('Sending test email…');
    try {
      const data = await apiPost('/api/messaging/email/test', {
        body: { to_email: testEmailAddr.trim() },
      });
      toast.dismiss(loadingId);
      toast.success(data.message || 'Test email sent — check your inbox.');
    } catch (e) {
      toast.dismiss(loadingId);
      toast.error(formatApiError(e, 'Test email failed'));
    } finally {
      setTestEmailSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-settings-page">
        <div>
          <h1 className="text-3xl font-bold mb-2">Platform Settings</h1>
          <p className="text-muted-foreground">Configure platform-wide settings and integrations</p>
        </div>

        <Tabs defaultValue="email" className="space-y-6">
          {/* Iter-53d: on mobile the 6-tab TabsList overflows viewport. Wrap
              in a horizontally-scrollable container with hidden scrollbar so
              users can swipe between tabs. `flex-nowrap` + `w-max` on the
              list keeps triggers full-size instead of Radix's default even
              distribution shrinkage. */}
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <TabsList className="inline-flex w-max min-w-full sm:w-auto">
            <TabsTrigger value="email">Email (SMTP2Go)</TabsTrigger>
            <TabsTrigger value="sms">SMS (Clicksend)</TabsTrigger>
            <TabsTrigger value="sender-ids" data-testid="sender-ids-tab">Sender IDs</TabsTrigger>
            <TabsTrigger value="payments">Payments (RyftPay)</TabsTrigger>
            <TabsTrigger value="affiliate">Affiliate Links</TabsTrigger>
            <TabsTrigger value="recent-sends" data-testid="recent-sends-tab">Recent Sends</TabsTrigger>
          </TabsList>
          </div>

          {/* Email Settings Tab */}
          <TabsContent value="email">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  SMTP2Go Configuration
                </CardTitle>
                <CardDescription>
                  Platform-level email service for password resets and system notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2">
                    <Badge variant={smtp2goConfigured ? 'default' : 'secondary'}>
                      {smtp2goConfigured ? 'Configured' : 'Not Configured'}
                    </Badge>
                    <p className="text-sm text-blue-800">
                      {smtp2goConfigured 
                        ? 'SMTP2Go is configured and ready to send emails' 
                        : 'Enter your SMTP2Go API key to enable system emails'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>SMTP2Go API Key</Label>
                  <Input 
                    type="password"
                    value={platformSettings.smtp2go_api_key}
                    onChange={(e) => setPlatformSettings({ ...platformSettings, smtp2go_api_key: e.target.value })}
                    placeholder={smtp2goConfigured ? "••••••••••••••••" : "api-XXXXXXXX"}
                  />
                  {smtp2goConfigured && (
                    <p className="text-xs text-emerald-600">API key is configured. Leave blank to keep existing key.</p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sender Email</Label>
                    <Input 
                      type="email"
                      value={platformSettings.smtp2go_sender_email}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, smtp2go_sender_email: e.target.value })}
                      placeholder="noreply@studioscheduler.ie"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sender Name</Label>
                    <Input 
                      value={platformSettings.smtp2go_sender_name}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, smtp2go_sender_name: e.target.value })}
                      placeholder="StudioScheduler"
                    />
                  </div>
                </div>

                <Button onClick={handleSavePlatformSettings} disabled={saving} data-testid="save-smtp2go-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Email Settings
                </Button>

                {/* Iter-46o: send-test-email — verify SMTP2Go in 5 seconds. */}
                <div className="border-t pt-6 mt-6 space-y-3" data-testid="test-email-section">
                  <div>
                    <h3 className="font-medium flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Test Email
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Verify your SMTP2Go credentials by sending a test to your own inbox.
                      Save first if you&rsquo;ve just changed the API key or sender details.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="email"
                      value={testEmailAddr}
                      onChange={(e) => setTestEmailAddr(e.target.value)}
                      placeholder="you@example.com"
                      className="sm:max-w-xs"
                      data-testid="test-email-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSendTestEmail}
                      disabled={testEmailSending || !smtp2goConfigured}
                      data-testid="send-test-email-btn"
                    >
                      {testEmailSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Test Email
                    </Button>
                  </div>
                  {!smtp2goConfigured && (
                    <p className="text-xs text-amber-600">
                      Save valid SMTP2Go credentials first to enable the test send.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMS Settings Tab */}
          <TabsContent value="sms">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  ClickSend Configuration
                </CardTitle>
                <CardDescription>
                  Platform-wide SMS provider. Used by every studio that doesn&rsquo;t have Brevo SMS turned on.
                  SMS is included in all plans &mdash; no per-studio top-ups required.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2">
                    <Badge variant={clicksendConfigured ? 'default' : 'secondary'}>
                      {clicksendConfigured ? 'Configured' : 'Not Configured'}
                    </Badge>
                    <p className="text-sm text-blue-800">
                      {clicksendConfigured 
                        ? 'ClickSend is the active platform SMS provider.' 
                        : 'Enter your ClickSend account credentials below to start sending SMS.'}
                    </p>
                  </div>
                </div>

                <div className="p-3 rounded-lg border bg-muted/30 text-xs space-y-1">
                  <p className="font-medium">Where to find these:</p>
                  <p>1. Log in at <a href="https://dashboard.clicksend.com" target="_blank" rel="noreferrer" className="underline text-primary">dashboard.clicksend.com</a> &rarr; <strong>Developers &rarr; API Credentials</strong>.</p>
                  <p>2. The <strong>Username</strong> is your ClickSend account login email/username (NOT the name shown on recipient&rsquo;s phone).</p>
                  <p>3. The <strong>API Key</strong> is the long string under &ldquo;API Key&rdquo;.</p>
                  <p>4. The <strong>Sender ID</strong> below is what recipients actually see &mdash; max 11 chars, letters/numbers only.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ClickSend Account Username</Label>
                    <Input 
                      value={platformSettings.clicksend_username}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, clicksend_username: e.target.value })}
                      placeholder={clicksendConfigured ? "••••••••" : "your ClickSend login"}
                      data-testid="clicksend-username-input"
                    />
                    <p className="text-xs text-muted-foreground">Your ClickSend account login &mdash; not the SMS sender name.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>ClickSend API Key</Label>
                    <Input 
                      type="password"
                      value={platformSettings.clicksend_api_key}
                      onChange={(e) => setPlatformSettings({ ...platformSettings, clicksend_api_key: e.target.value })}
                      placeholder={clicksendConfigured ? "••••••••••••••••" : "your API key"}
                      data-testid="clicksend-api-key-input"
                    />
                    <p className="text-xs text-muted-foreground">From dashboard.clicksend.com &rarr; Developers &rarr; API Credentials.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Sender ID (shown on recipient's phone)</Label>
                  <Input 
                    value={platformSettings.clicksend_sender_id || ''}
                    onChange={(e) => setPlatformSettings({ ...platformSettings, clicksend_sender_id: e.target.value })}
                    placeholder="StudioSched"
                    maxLength={11}
                    data-testid="clicksend-sender-id-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Max 11 characters, alphanumeric, no spaces. This is what shows up as the &ldquo;from&rdquo; on every SMS your platform sends.
                    Leave blank to use <code>StudioSched</code>.
                  </p>
                </div>

                {clicksendConfigured && (
                  <p className="text-xs text-emerald-600">Existing credentials are masked. Leave blank to keep current values; type a new value to overwrite.</p>
                )}

                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <Button onClick={handleSavePlatformSettings} disabled={saving} data-testid="save-clicksend-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save SMS Settings
                  </Button>
                </div>

                {/* Iter-46n: send-test-SMS — verify credentials in 5 seconds. */}
                <div className="border-t pt-6 mt-6 space-y-3" data-testid="test-sms-section">
                  <div>
                    <h3 className="font-medium flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Test SMS
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Verify your ClickSend credentials by sending a quick test to your own phone.
                      Save first if you&rsquo;ve just changed the username, key, or sender ID.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="tel"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="+353871234567"
                      className="sm:max-w-xs"
                      data-testid="test-sms-phone-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSendTestSms}
                      disabled={testSending || !clicksendConfigured}
                      data-testid="send-test-sms-btn"
                    >
                      {testSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Test SMS
                    </Button>
                  </div>
                  {!clicksendConfigured && (
                    <p className="text-xs text-amber-600">
                      Save valid ClickSend credentials first to enable the test send.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sender ID Requests Tab — Iter-46s */}
          <TabsContent value="sender-ids">
            <AdminSenderIdRequests />
          </TabsContent>

          {/* RyftPay Settings Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  RyftPay Configuration
                </CardTitle>
                <CardDescription>
                  Platform-level payment processing settings for RyftPay integration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Environment Mode Toggle */}
                <div className="p-4 rounded-lg border bg-muted">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium">Environment Mode</Label>
                      <p className="text-sm text-muted-foreground">
                        Switch between sandbox (testing) and production (live) modes
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${platformSettings.ryftpay_mode === 'sandbox' ? 'font-semibold text-amber-600' : 'text-muted-foreground'}`}>
                        Sandbox
                      </span>
                      <Switch 
                        checked={platformSettings.ryftpay_mode === 'production'}
                        onCheckedChange={(checked) => setPlatformSettings({ 
                          ...platformSettings, 
                          ryftpay_mode: checked ? 'production' : 'sandbox' 
                        })}
                        data-testid="ryftpay-mode-switch"
                      />
                      <span className={`text-sm ${platformSettings.ryftpay_mode === 'production' ? 'font-semibold text-emerald-600' : 'text-muted-foreground'}`}>
                        Production
                      </span>
                    </div>
                  </div>
                </div>

                {/* Mode indicator */}
                <div className={`p-3 rounded-lg border ${platformSettings.ryftpay_mode === 'production' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-center gap-2">
                    <Badge variant={platformSettings.ryftpay_mode === 'production' ? 'default' : 'outline'} 
                           className={platformSettings.ryftpay_mode === 'production' ? 'bg-emerald-500' : 'bg-amber-100 text-amber-800 border-amber-300'}>
                      {platformSettings.ryftpay_mode === 'production' ? 'PRODUCTION' : 'SANDBOX'}
                    </Badge>
                    <p className={`text-sm ${platformSettings.ryftpay_mode === 'production' ? 'text-emerald-800' : 'text-amber-800'}`}>
                      {platformSettings.ryftpay_mode === 'production' 
                        ? 'Live mode - Real transactions will be processed' 
                        : 'Test mode - No real transactions will be processed'}
                    </p>
                  </div>
                </div>

                {/* Sandbox API Key */}
                <div className="space-y-2 p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Sandbox Secret Key</Label>
                    <Badge variant={ryftpaySandboxConfigured ? 'default' : 'secondary'}>
                      {ryftpaySandboxConfigured ? 'Configured' : 'Not Configured'}
                    </Badge>
                  </div>
                  <Input 
                    type="password"
                    value={platformSettings.ryftpay_sandbox_api_key}
                    onChange={(e) => setPlatformSettings({ ...platformSettings, ryftpay_sandbox_api_key: e.target.value })}
                    placeholder={ryftpaySandboxConfigured ? "••••••••••••••••" : "sk_sandbox_..."}
                    data-testid="ryftpay-sandbox-key-input"
                  />
                  {ryftpaySandboxConfigured && (
                    <p className="text-xs text-emerald-600">Sandbox key is configured. Leave blank to keep existing.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Secret key for server-side API calls. Get from RyftPay sandbox portal.
                  </p>
                </div>

                {/* Sandbox Public Key */}
                <div className="space-y-2 p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Sandbox Public Key</Label>
                    {ryftpaySandboxPublicKeyConfigured && (
                      <Badge variant="default">Configured</Badge>
                    )}
                  </div>
                  <Input 
                    type="text"
                    value={platformSettings.ryftpay_sandbox_public_key}
                    onChange={(e) => setPlatformSettings({ ...platformSettings, ryftpay_sandbox_public_key: e.target.value })}
                    placeholder={ryftpaySandboxPublicKeyConfigured ? "pk_sandbox_••••••••" : "pk_sandbox_..."}
                    data-testid="ryftpay-sandbox-public-key-input"
                  />
                  {ryftpaySandboxPublicKeyConfigured && (
                    <p className="text-xs text-emerald-600">Sandbox public key is configured. Leave blank to keep existing.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Public key for client-side card tokenization. Required for saved cards feature.
                  </p>
                </div>

                {/* Production API Key */}
                <div className="space-y-2 p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Production Secret Key</Label>
                    <Badge variant={ryftpayProductionConfigured ? 'default' : 'secondary'}>
                      {ryftpayProductionConfigured ? 'Configured' : 'Not Configured'}
                    </Badge>
                  </div>
                  <Input 
                    type="password"
                    value={platformSettings.ryftpay_production_api_key}
                    onChange={(e) => setPlatformSettings({ ...platformSettings, ryftpay_production_api_key: e.target.value })}
                    placeholder={ryftpayProductionConfigured ? "••••••••••••••••" : "sk_live_..."}
                    data-testid="ryftpay-production-key-input"
                  />
                  {ryftpayProductionConfigured && (
                    <p className="text-xs text-emerald-600">Production key is configured. Leave blank to keep existing.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Secret key for live transactions. Get from{' '}
                    <a href="https://portal.ryftpay.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      portal.ryftpay.com
                    </a>
                  </p>
                </div>

                {/* Production Public Key */}
                <div className="space-y-2 p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Production Public Key</Label>
                    {ryftpayProductionPublicKeyConfigured && (
                      <Badge variant="default">Configured</Badge>
                    )}
                  </div>
                  <Input 
                    type="text"
                    value={platformSettings.ryftpay_production_public_key}
                    onChange={(e) => setPlatformSettings({ ...platformSettings, ryftpay_production_public_key: e.target.value })}
                    placeholder={ryftpayProductionPublicKeyConfigured ? "pk_live_••••••••" : "pk_live_..."}
                    data-testid="ryftpay-production-public-key-input"
                  />
                  {ryftpayProductionPublicKeyConfigured && (
                    <p className="text-xs text-emerald-600">Production public key is configured. Leave blank to keep existing.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Public key for client-side card tokenization in production.
                  </p>
                </div>

                {/* Active Status */}
                <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2">
                    <Badge variant={ryftpayConfigured ? 'default' : 'secondary'}>
                      {ryftpayConfigured ? 'Active Mode Configured' : 'Active Mode Not Configured'}
                    </Badge>
                    <p className="text-sm text-blue-800">
                      {ryftpayConfigured 
                        ? `The ${platformSettings.ryftpay_mode} API key is configured and active` 
                        : `Please configure the ${platformSettings.ryftpay_mode} API key to enable payments`}
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-lg border bg-blue-50 border-blue-200 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label className="text-base font-medium">Pass Processing Fees to Sub-Accounts</Label>
                      <p className="text-sm text-blue-800 mt-1">
                        When ON, RyftPay's gateway / processor / interchange / network / pass-through fees are
                        charged to the studio's sub-account instead of the platform. Studios receive a slightly
                        smaller payout; the platform's net margin increases.
                      </p>
                    </div>
                    <Switch
                      checked={!!platformSettings.pass_processing_fees_to_subaccount}
                      onCheckedChange={(checked) => setPlatformSettings({
                        ...platformSettings,
                        pass_processing_fees_to_subaccount: checked
                      })}
                      data-testid="pass-processing-fees-toggle"
                    />
                  </div>
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    <strong>Important:</strong> RyftPay must enable the "Pass processing fees to Sub-Accounts"
                    feature on your platform account before this toggle can be used. Email RyftPay support with
                    your account ID to request activation. Until then, leave this OFF or every payment will be
                    rejected with a 400 <code>invalid_field</code> error.
                  </p>
                </div>

                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Studios must complete their own onboarding to receive payouts.
                  </p>
                </div>

                <Button onClick={handleSavePlatformSettings} disabled={saving} data-testid="save-ryftpay-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Payment Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Affiliate Settings Tab */}
          <TabsContent value="affiliate">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LinkIcon className="w-5 h-5" />
                  Affiliate Links
                </CardTitle>
                <CardDescription>
                  Configure affiliate/referral links shown to studio owners
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Brevo Affiliate Link</Label>
                  <Input 
                    value={affiliateSettings.brevo_affiliate_link}
                    onChange={(e) => setAffiliateSettings({ ...affiliateSettings, brevo_affiliate_link: e.target.value })}
                    placeholder="https://www.brevo.com/?tap_a=XXXXX"
                  />
                  <p className="text-xs text-muted-foreground">
                    This link is shown to studio owners when they set up Brevo integration
                  </p>
                </div>

                {affiliateSettings.brevo_affiliate_link && (
                  <a 
                    href={affiliateSettings.brevo_affiliate_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Preview Link
                  </a>
                )}

                <Button onClick={handleSaveAffiliateSettings} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Affiliate Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Iter-48: Recent Platform Sends — full audit of email/SMS/WhatsApp */}
          <TabsContent value="recent-sends">
            <PlatformSendsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};
